import React, { useState, useEffect, useRef, useCallback } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { listenSafe as listen } from '../utils/safeTauriEvent';
import { open } from '@tauri-apps/plugin-dialog';
import { RefreshCw, FileCode } from 'lucide-react';
import CodeEditor from './ui/CodeEditor';
import ResizableSplitter from './ui/ResizableSplitter';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { KvTable, KvRow } from './ui/KvTable';
import { ensureTrailingEmptyKvRow } from '../utils/kvRows';
import StreamRequestBar from './ui/StreamRequestBar';
import StreamLogTable from './ui/StreamLogTable';
import './streamBuilders.css';
import { recordFeature, REQUEST } from '../services/telemetry';
import { getGrpcTabState, setGrpcTabState } from '../utils/grpcTabState';
import { formatJson } from '../utils/formatCode';
import { useWorkspaceStore } from '../store/workspaceStore';
import { protoPathToLoad, protoPathToSave, workspaceFolderOf } from '../utils/grpcProtoPath';
import { prepareGrpcCall } from '../utils/protocolVariables';

const GRPC_STATUS_NAMES = {
  0: 'OK',
  1: 'CANCELLED',
  2: 'UNKNOWN',
  3: 'INVALID_ARGUMENT',
  4: 'DEADLINE_EXCEEDED',
  5: 'NOT_FOUND',
  7: 'PERMISSION_DENIED',
  12: 'UNIMPLEMENTED',
  14: 'UNAVAILABLE',
  16: 'UNAUTHENTICATED',
};

function grpcStatusLabel(code) {
  return GRPC_STATUS_NAMES[code] ?? `STATUS_${code}`;
}


// formatJson, not parse → stringify: int64 / uint64 fields above 2^53 keep every digit.
function prettyJson(str) {
  try {
    return formatJson(str);
  } catch {
    return str;
  }
}

// {{variables}} in the address, metadata keys/values and the message JSON are resolved
// with the HTTP resolver over `variableScopes` (utils/protocolVariables) — as typed in
// the fields, substituted only on the way to Rust.
const GRPCBuilder = ({ request, onDataChange, variableScopes }) => {
  const [address, setAddress] = useState(request?.grpcAddress ?? '');
  const [useTLS, setUseTLS] = useState(request?.grpcUseTLS ?? false);
  // A folder workspace saves the .proto relative to its folder (grpcProtoPath.js).
  const folder = workspaceFolderOf(useWorkspaceStore((s) => s.currentWorkspace));
  // Keyed by tab (see RequestBuilder): what this tab loaded survives a tab switch; a
  // fresh tab starts from what the saved request remembers (card 5258c71d).
  const tabState = getGrpcTabState(request?.id);
  const [protoDescriptor, setProtoDescriptor] = useState(() => tabState?.descriptor ?? null);
  const [service, setService] = useState(() => tabState?.service ?? request?.grpcService ?? null);
  const [method, setMethod] = useState(() => tabState?.method ?? request?.grpcMethod ?? null);
  // The .proto the definition came from (null = server reflection). Calls encode with
  // it, so a server without reflection can be called once its .proto is loaded.
  const [protoPath, setProtoPath] = useState(() => tabState?.protoPath ?? protoPathToLoad(request?.protoPath, folder));
  // A saved RELATIVE .proto path with no workspace folder to resolve it against is
  // kept as saved — an edit to another field must not erase it. Picking a file or
  // using server reflection replaces it on purpose.
  const [unresolvedProto, setUnresolvedProto] = useState(() =>
    !tabState?.protoPath && request?.protoPath && !protoPathToLoad(request.protoPath, folder) ? request.protoPath : null
  );
  useEffect(() => {
    setGrpcTabState(request?.id, { descriptor: protoDescriptor, service, method, protoPath });
  }, [request?.id, protoDescriptor, service, method, protoPath]);
  const [callType, setCallType] = useState('unary');
  const [requestJson, setRequestJson] = useState(request?.grpcRequestJson ?? '{\n  \n}');
  // Always ends with ONE empty row: type in it and the next appears — the HTTP builder's
  // table, no "Add metadata" button. Rows without a key are never sent.
  const withTrailingRow = (rows) => ensureTrailingEmptyKvRow(rows, 'grpcm').map(({ key, value, enabled, _id }) => ({ key, value, enabled: enabled !== false, _id }));
  const [metadata, setMetadata] = useState(() => withTrailingRow(request?.grpcMetadata));
  const [status, setStatus] = useState('idle');
  const [responses, setResponses] = useState([]);
  const [singleResponse, setSingleResponse] = useState(null);
  const [grpcStatus, setGrpcStatus] = useState(null);
  const [trailers, setTrailers] = useState({});
  const [duration, setDuration] = useState(null);
  const [error, setError] = useState(null);

  const streamIdRef = useRef(null);
  const unlistenRef = useRef([]);

  // Keep parent in sync — on EDITS, never on mount. The parent merges this patch over
  // its own field state (`tabFieldsRef`), and when this builder mounts for a tab that was
  // just opened, that state still belongs to the PREVIOUS tab (a child's effects run
  // before the parent's): the new gRPC tab got the other tab's name, URL, method and
  // headers written into it. Seen 19.09.2026 as a gRPC tab titled "New SSE stream".
  // Compared by VALUE, not "skip the first run": StrictMode runs mount effects twice.
  const reportedRef = useRef(null);
  useEffect(() => {
    const now = JSON.stringify([address, useTLS, requestJson, metadata, protoPath, service, method]);
    const before = reportedRef.current;
    reportedRef.current = now;
    if (before === null || before === now) return;
    onDataChange?.({
      grpcAddress: address,
      grpcUseTLS: useTLS,
      grpcRequestJson: requestJson,
      grpcMetadata: metadata,
      protoPath: protoPath ? protoPathToSave(protoPath, folder) : unresolvedProto,
      grpcService: service,
      grpcMethod: method,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires on edits only; the parent passes a fresh onDataChange each render, so tracking it would loop
  }, [address, useTLS, requestJson, metadata, protoPath, service, method]);

  // A saved request with a .proto: load it again when the tab opens (the definition
  // itself is never saved — it is what the file says now).
  useEffect(() => {
    // Only a .proto file is opened by itself — a pulled repo could name any path.
    if (protoDescriptor || !protoPath || !/\.proto$/i.test(protoPath)) return undefined;
    let live = true;
    invoke('load_proto_file', { protoPath })
      .then((descriptor) => {
        if (live) setProtoDescriptor(descriptor);
      })
      .catch((e) => {
        if (live) setError(`The saved .proto could not be loaded (${protoPath}): ${e}`);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per tab: a picked file loads in handleLoadProto
  }, []);


  // Set callType based on method selection
  useEffect(() => {
    if (!service || !method || !protoDescriptor) return;
    const svcInfo = protoDescriptor.services.find((s) => s.name === service);
    const mInfo = svcInfo?.methods.find((m) => m.name === method);
    if (mInfo) {
      setCallType(mInfo.server_streaming ? 'server_stream' : 'unary');
    }
  }, [service, method, protoDescriptor]);

  const stopStream = useCallback(async () => {
    if (streamIdRef.current) {
      try {
        await invoke('cancel_grpc_stream', { streamId: streamIdRef.current });
      } catch (_) {}
      streamIdRef.current = null;
    }
    unlistenRef.current.forEach((fn) => fn());
    unlistenRef.current = [];
    setStatus('idle');
  }, []);

  const handleLoadProto = async () => {
    try {
      const selected = await open({
        multiple: false,
        filters: [{ name: 'Proto Files', extensions: ['proto'] }],
      });
      if (!selected) return;
      const descriptor = await invoke('load_proto_file', { protoPath: selected });
      setProtoDescriptor(descriptor);
      setProtoPath(selected);
      setUnresolvedProto(null);
      setService(null);
      setMethod(null);
      setError(null);
    } catch (e) {
      setError(`Failed to load .proto: ${e}`);
    }
  };

  const handleReflect = async () => {
    if (!address) return;
    setStatus('calling');
    setError(null);
    try {
      const descriptor = await invoke('reflect_grpc_server', { address: prepareGrpcCall({ address }, variableScopes).address, useTls: useTLS });
      setProtoDescriptor(descriptor);
      setProtoPath(null);
      setUnresolvedProto(null);
      // A saved method the server still has stays picked (a request saved via reflection).
      const svc = (descriptor?.services || []).find((s) => s.name === service);
      if (!svc || !(svc.methods || []).some((m) => m.name === method)) {
        setService(null);
        setMethod(null);
      }
    } catch (e) {
      setError(`Server reflection failed: ${e}`);
    } finally {
      setStatus('idle');
    }
  };

  const handleInvoke = async () => {
    recordFeature(REQUEST.GRPC);   // identifier-free: this kind was used today
    if (!address || !service || !method) {
      setError('Address, service, and method are required.');
      return;
    }
    // A saved request remembers its method, not the definition: without it the
    // call type (unary / streaming) is unknown, so it is loaded first.
    if (!protoDescriptor) {
      setError('Load the service definition first (Server Reflection or a .proto file).');
      setConfigTab('definition');
      return;
    }

    setError(null);
    setSingleResponse(null);
    setResponses([]);
    setGrpcStatus(null);
    setTrailers({});
    setDuration(null);

    // unticked / keyless metadata rows stay in the table, unsent
    const sent = prepareGrpcCall({ address, requestJson, metadata }, variableScopes);

    if (callType === 'unary') {
      setStatus('calling');
      try {
        const result = await invoke('call_grpc_unary', {
          address: sent.address,
          service,
          method,
          requestJson: sent.requestJson,
          metadata: sent.metadata,
          useTls: useTLS,
          protoPath,
        });
        setSingleResponse(prettyJson(result.message_json));
        setGrpcStatus(result.grpc_status);
        setTrailers(result.trailers);
        setDuration(result.duration_ms);
        setStatus('done');
      } catch (e) {
        setError(String(e));
        setStatus('error');
      }
    } else {
      setStatus('streaming');
      const id = `grpc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      streamIdRef.current = id;

      const unlisten1 = await listen(`grpc-stream-${id}`, (evt) => {
        // in the log table's shape (ui/StreamLogTable)
        setResponses((prev) => [
          ...prev,
          { id: `grpc-${id}-${prev.length}`, kind: 'message', direction: 'in', data: prettyJson(evt.payload), timestamp: Date.now() },
        ]);
      });
      const unlisten2 = await listen(`grpc-stream-${id}-done`, () => {
        setStatus('done');
        streamIdRef.current = null;
        unlistenRef.current.forEach((fn) => fn());
        unlistenRef.current = [];
      });
      const unlisten3 = await listen(`grpc-stream-${id}-error`, (evt) => {
        setError(String(evt.payload));
        setStatus('error');
        streamIdRef.current = null;
        unlistenRef.current.forEach((fn) => fn());
        unlistenRef.current = [];
      });
      unlistenRef.current = [unlisten1, unlisten2, unlisten3];

      try {
        await invoke('call_grpc_server_stream', {
          address: sent.address,
          service,
          method,
          requestJson: sent.requestJson,
          metadata: sent.metadata,
          useTls: useTLS,
          streamId: id,
          protoPath,
        });
      } catch (e) {
        setError(String(e));
        setStatus('error');
        unlistenRef.current.forEach((fn) => fn());
        unlistenRef.current = [];
        streamIdRef.current = null;
      }
    }
  };

  const updateMetadataRow = (idx, field, val) =>
    setMetadata((prev) => {
      const next = prev.map((r, i) => (i === idx ? { ...r, [field]: val } : r));
      // the last row got a key → open the next empty one
      return next[next.length - 1].key !== '' ? [...next, { key: '', value: '', enabled: true, _id: `grpcm-${Date.now()}` }] : next;
    });

  const removeMetadataRow = (idx) =>
    setMetadata((prev) => withTrailingRow(prev.filter((_, i) => i !== idx)));

  const services = protoDescriptor?.services ?? [];

  // ONE picker, "Service / Method" — like the HTTP builder's single method control.
  const methodOptions = services.flatMap((svc) =>
    (svc.methods || []).map((m) => ({
      value: `${svc.name}/${m.name}`,
      label: `${svc.name.split('.').pop()} / ${m.name}${m.server_streaming ? '  (stream)' : ''}`,
    })));
  const pickMethod = (value) => {
    const at = value.lastIndexOf('/');
    const svcName = value.slice(0, at);
    const methodName = value.slice(at + 1);
    setService(svcName);
    setMethod(methodName);
    const picked = services.find((x) => x.name === svcName)?.methods?.find((x) => x.name === methodName);
    if (picked) setCallType(picked.server_streaming ? 'server_stream' : 'unary');
  };
  const [configTab, setConfigTab] = useState('message');

  const isRunning = status === 'calling' || status === 'streaming';

  const statusColor =
    status === 'done'
      ? 'text-green-600'
      : status === 'error'
      ? 'text-red-500'
      : status === 'streaming'
      ? 'text-blue-500'
      : 'text-gray-400';

  return (
    <div className="h-full flex flex-col bg-white">
      {/* ── Request bar — the HTTP builder's: address + method in one field group, Invoke where Send is ── */}
      <StreamRequestBar
        inputProps={{
          className: 'grpc-address-input',
          placeholder: 'grpc.example.com:443',
          value: address,
          onChange: (e) => setAddress(e.target.value),
          'aria-label': 'gRPC server address',
        }}
        trailing={services.length ? (
          <CompactSelectPicker
            value={service && method ? `${service}/${method}` : null}
            onChange={pickMethod}
            options={methodOptions}
            placeholder="Select a method"
            searchable={methodOptions.length > 8}
            menuMinWidth={280}
            aria-label="gRPC method"
          />
        ) : (
          <button type="button" className="grpc-load-definition" onClick={() => setConfigTab('definition')}>
            {service && method ? `${service}/${method} — load the definition…` : 'Load a service definition…'}
          </button>
        )}
        action={isRunning
          ? { label: 'Stop', onClick: stopStream, danger: true }
          : { label: 'Invoke', onClick: handleInvoke, disabled: !address || !service || !method || !protoDescriptor }}
      />

      {/* ── Request above, response below — the HTTP builder's split ── */}
      <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        <ResizableSplitter direction="vertical" initialSplit={45} minSize={0} maxSize={100} minFirstPanelPx={60} minSecondPanelPx={60} className="h-full">
          <div className="bg-white flex flex-col h-full" style={{ minHeight: 0 }}>
            <div className="sb-tabs flex-shrink-0">
              {[
                ['message', 'Message'],
                ['metadata', `Metadata${metadata.filter((r) => r.key).length ? ` (${metadata.filter((r) => r.key).length})` : ''}`],
                ['definition', 'Service definition'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setConfigTab(key)}
                  className={`sb-tab${configTab === key ? ' sb-tab--active' : ''}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-auto" style={{ minHeight: 0 }}>
              {configTab === 'message' && (
                // the same air above the editor as the HTTP builder's Body / Script tabs
                <div className="sb-editor">
                  <CodeEditor hideChrome value={requestJson} onChange={setRequestJson} language="json" height="100%" />
                </div>
              )}

              {configTab === 'metadata' && (
                <div className="editor-tab-body">
                    <KvTable>
                      {metadata.map((row, idx) => (
                        <KvRow
                          key={row._id ?? idx}
                          enabled={row.enabled !== false}
                          onToggleEnabled={(v) => updateMetadataRow(idx, 'enabled', v)}
                          checkboxTabIndex={-1}
                          showRemove={!(idx === metadata.length - 1 && row.key === '')}
                          onRemove={() => removeMetadataRow(idx)}
                          renderKey={
                            <input
                              type="text"
                              value={row.key}
                              onChange={(e) => updateMetadataRow(idx, 'key', e.target.value)}
                              placeholder="Key"
                              aria-label="Metadata key"
                              className="request-builder-kv-input w-full rounded-md focus:outline-none"
                            />
                          }
                          renderValue={
                            <input
                              type="text"
                              value={row.value}
                              onChange={(e) => updateMetadataRow(idx, 'value', e.target.value)}
                              placeholder="Value"
                              aria-label="Metadata value"
                              className="request-builder-kv-input w-full rounded-md focus:outline-none"
                            />
                          }
                        />
                      ))}
                    </KvTable>
                </div>
              )}

              {configTab === 'definition' && (
                <div className="px-5 py-3 flex flex-col gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button type="button" onClick={handleReflect} disabled={!address || isRunning} className="sb-btn">
                      <RefreshCw size={13} /> Server Reflection
                    </button>
                    <button type="button" onClick={handleLoadProto} className="sb-btn">
                      <FileCode size={13} /> Load .proto
                    </button>
                    <span className="grpc-definition-note">
                      {services.length
                        ? `${services.length} service${services.length === 1 ? '' : 's'} · ${methodOptions.length} method${methodOptions.length === 1 ? '' : 's'}`
                        : 'Nothing loaded yet — reflect the server, or load its .proto file.'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <label className="sb-check">
                      <input type="checkbox" checked={useTLS} onChange={(e) => setUseTLS(e.target.checked)} className="checkbox-brand cursor-pointer" />
                      TLS
                    </label>
                    <div className="sb-segment" role="radiogroup" aria-label="Call type">
                      {[['unary', 'Unary'], ['server_stream', 'Server stream']].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          role="radio"
                          aria-checked={callType === value}
                          onClick={() => setCallType(value)}
                          className={`sb-segment__option${callType === value ? ' sb-segment__option--active' : ''}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Response */}
          <div className="bg-white flex flex-col h-full response-section" style={{ minHeight: 0 }}>
            <div className="sb-pane-head flex-shrink-0">
              <span>Response</span>
              <div className="flex items-center gap-2 text-xs" style={{ textTransform: 'none', letterSpacing: 0 }}>
                {grpcStatus !== null && (
                  <span className={`px-2 py-1 rounded-full font-medium ${grpcStatus === 0 ? 'status-success' : 'status-error'}`}>
                    {grpcStatusLabel(grpcStatus)}
                  </span>
                )}
                {duration !== null && <span className="text-gray-600 font-medium">{duration}ms</span>}
                <span className={`font-medium capitalize ${statusColor}`}>{status}</span>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {error ? (
                <div className="p-3 text-xs text-red-600 font-mono whitespace-pre-wrap">{error}</div>
              ) : callType === 'server_stream' && responses.length > 0 ? (
                /* a stream is a log — the WebSocket/SSE table: a row per message, a click opens it highlighted */
                <div className="px-5 pt-2">
                  <StreamLogTable
                    entries={responses}
                    filters={['all']}
                    onClear={() => setResponses([])}
                    emptyText=""
                  />
                </div>
              ) : singleResponse ? (
                <div className="sb-editor h-full"><CodeEditor hideChrome readOnly value={singleResponse} onChange={() => {}} language="json" height="100%" /></div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-xs">
                  Invoke a method to see the response
                </div>
              )}
            </div>

            {Object.keys(trailers).length > 0 && (
              <div className="flex-shrink-0 border-t border-gray-200 px-3 py-2">
                <p className="text-xs font-semibold text-gray-500 mb-1">Trailers</p>
                <div className="space-y-0.5">
                  {Object.entries(trailers).map(([k, v]) => (
                    <div key={k} className="text-xs font-mono">
                      <span className="text-blue-600">{k}</span>
                      <span className="text-gray-400">: </span>
                      <span className="text-gray-700">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ResizableSplitter>
      </div>
    </div>
  );
};

export default GRPCBuilder;
